
import { EXTRA_GUARANTEE_ATTRIBUTES } from '~~/constants/extraGuarantee';
import type { GuaranteeOption } from '~~/types/extraGuarantee/guaranteeOption';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';

export const useExtraGuaranteeOptions = (product?: ProductVariantExtended) => {

  const { languageAndCountry, currencyCode } = useLocalization();
  const { productPriceForExtraGuarantee, productForExtraGuarantee } = useExtraGuarantee();
  const { getGuaranteesForProduct } = useExtraGuaranteeExcluded();

  const formatPrice = (extraGuaranteePrice: number) => {

    const formattedPrice = extraGuaranteePrice.toLocaleString(languageAndCountry.value, {
      style: 'currency',
      currency: currencyCode.value,
      maximumFractionDigits: 2,
    });

    return formattedPrice;
  
  };

  const guaranteeOptions = computed<GuaranteeOption[]>(() => {

    const productPrice = productPriceForExtraGuarantee.value;

    if (!productPrice) {

      return [];
    
    }

    const filteredExtraGuaranteeProducts = getGuaranteesForProduct(product || productForExtraGuarantee.value);

    const parsedGuaranteeOptions = filteredExtraGuaranteeProducts?.map((el: ProductVariantExtended) => {

      const priceRate = el.attributesRaw.find(attr =>
        attr.name === EXTRA_GUARANTEE_ATTRIBUTES.EXTRA_GUARANTEE_RATE);

      if (!priceRate) {

        return null;
      
      }

      const price = productPrice * (priceRate.value / 100);

      return {
        name: el._name,
        priceRate: priceRate.value,
        formattedPrice: formatPrice(price),
        price,
        sku: el.sku,
      };
    
    }) ?? [];

    return parsedGuaranteeOptions
      .filter((option): option is GuaranteeOption => option !== null)
      .sort((a, b) => (b.priceRate - a.priceRate));
  
  });

  const highestGuaranteeOptionSku = computed<Maybe<string>>(() => {

    if (!guaranteeOptions.value?.length) {

      return;
    
    }

    return guaranteeOptions.value[0]?.sku;
  
  });

  return {
    guaranteeOptions,
    highestGuaranteeOptionSku,
  };

};
